/*
 * <lazy-product-row> fills a product row (snippets/lazy-product-row.liquid)
 * as the shopper scrolls towards it, the way Horizon's product
 * recommendations do: Shopify renders the row's section with the product
 * cards (Section Rendering API), and its cards take the place of the
 * placeholders, which hold the row's height meanwhile, so a shopper scrolling
 * past never has the page grow or jump under them.
 *
 * - `related`: the recommendations endpoint renders the section with
 *   Shopify's related products.
 * - `recently_viewed`: product IDs this browser viewed (localStorage, newest
 *   first, up to 12; on a product page the current product is left out and
 *   then remembered). A search for those IDs renders the section, as
 *   Horizon's search does for its recently viewed products, and the cards
 *   are put back in viewing order.
 *
 * The row hides when there's nothing to show.
 */

const RECENT_KEY = 'recently-viewed-products';
const RECENT_LIMIT = 12;
const ROW_LIMIT = 8;

function recentlyViewed() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RECENT_KEY));
    return Array.isArray(stored) ? stored.map(String) : [];
  } catch {
    return [];
  }
}

function rememberViewed(productId) {
  const ids = [productId, ...recentlyViewed().filter((id) => id !== productId)].slice(0, RECENT_LIMIT);
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode, blocked): the row just stays empty.
  }
}

// A page a browser prerenders only counts as viewed once it's opened.
function whenShown(callback) {
  if (document.prerendering) document.addEventListener('prerenderingchange', callback, { once: true });
  else callback();
}

class LazyProductRow extends HTMLElement {
  #observer;

  connectedCallback() {
    this.list = this.querySelector('[data-product-row-cards]');
    if (!this.list || !this.dataset.url) return;
    const current = this.dataset.productId;

    if (this.dataset.source === 'recently_viewed') {
      this.ids = recentlyViewed()
        .filter((id) => id !== current)
        .slice(0, ROW_LIMIT);
      if (current) whenShown(() => rememberViewed(current));
      if (this.ids.length === 0) return;
      // Takes its room now, far below the fold, rather than while the shopper scrolls past.
      this.hidden = false;
    }

    // About two screens early, so the cards are usually in before the row is in view.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.#observer.disconnect();
        this.#load();
      },
      { rootMargin: '1500px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #load() {
    const items = await this.#cards().catch(() => []);
    // Nothing to show (or network trouble): the row gives its room back.
    if (items.length === 0) {
      this.hidden = true;
      return;
    }

    // The cards take the placeholders' place at the same height.
    this.list.replaceChildren(...items);
    this.hidden = false;
    this.querySelector('scroll-row')?.refresh?.();
  }

  async #cards() {
    const url = new URL(this.dataset.url, window.location.origin);
    if (this.ids) url.searchParams.set('q', this.ids.map((id) => `id:${id}`).join(' OR '));

    const response = await fetch(url);
    if (!response.ok) return [];

    const template = document.createElement('template');
    template.innerHTML = await response.text();
    const items = [...(template.content.querySelector('[data-product-row-cards]')?.children ?? [])];
    if (!this.ids) return items;

    // A search lists its results by relevance: back in viewing order.
    const byId = new Map(items.map((item) => [item.querySelector('[data-product-id]')?.dataset.productId, item]));
    return this.ids.map((id) => byId.get(String(id))).filter(Boolean);
  }
}

if (!customElements.get('lazy-product-row')) customElements.define('lazy-product-row', LazyProductRow);
